import React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../generic/systemValues/systemValueLabel'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { isShortStatusChange } from '../statusTimeline'
import { isBlank } from '../detailShared'

// S422 — la tabla de CAMBIOS DE ESTADO de una operación: cuándo, a qué estado, por qué, y quién
// abrió y quién cerró cada tramo. Sustituye al histórico de interacciones; es otra pregunta —«quién
// cambió el estado y por qué» y no «quién trabajó y cuánto»— y por eso NO trae duración ni
// incidencia, por decisión del PO.
//
// ⚠️ LO QUE NO SE HACE AQUÍ:
//   · NO SE REORDENA. Sun sirve el orden y filas que empatan en la fecha harían que un cliente que
//     pagine repita y pierda filas si las reordenara.
//   · NO SE CALCULA UNA COLUMNA DE DURACIÓN, aunque se podría restando las dos fechas. El PO quitó la
//     duración de esta pantalla a sabiendas, y una columna calculada la devolvería por la puerta de
//     atrás — con el agravante de que no cuadraría con el tiempo real de la cabecera, que sale de la
//     otra tabla. La duración vive solo en el resumen por estado, donde se dice qué es.

const StatusChangeTable = ({ statusChanges = [], includeShort = false }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const notReported = t_('Not reported')
  const rows = (statusChanges || []).filter((change) => includeShort || !isShortStatusChange(change))
  // ⚠️ RN8 SIN UNA COLUMNA DE RAYAS: en la base, `reason` viene null en los 195 tramos medidos
  // (LTT001-HUN001). Una columna entera diciendo «sin informar» en cada fila no informa de nada, así
  // que si NINGÚN tramo trae motivo la ausencia se declara UNA vez, debajo, y la columna no se pinta.
  // Se mira sobre TODOS los tramos, no sobre los visibles: si no, pulsar el filtro de cortos haría
  // aparecer y desaparecer la columna.
  const anyReason = (statusChanges || []).some((change) => !isBlank((change.reason || {}).name) || !isBlank((change.reason || {}).code))

  if ((statusChanges || []).length === 0) {
    return (
      <Typography data-testid='status-changes-empty' variant='body2' sx={{ color: 'text.secondary' }}>
        {t_('No status changes recorded')}
      </Typography>
    )
  }

  // Every change is a short one and the filter hides them: the table would be an empty frame.
  if (rows.length === 0) {
    return (
      <Typography data-testid='status-changes-none-shown' variant='body2' sx={{ color: 'text.secondary' }}>
        {t_('No status changes to show with the current filter')}
      </Typography>
    )
  }

  const personOf = (worker) => {
    const w = worker || {}
    if (isBlank(w.name) && isBlank(w.code)) return notReported
    return `${w.code ? `${w.code} · ` : ''}${w.name || ''}`.replace(/ · $/, '')
  }

  return (
    <TableContainer data-testid='status-change-table'>
      <Table size='small' aria-label={t_('Status changes')}>
        <TableHead>
          <TableRow>
            <TableCell>{t_('Changed at')}</TableCell>
            <TableCell>{t_('Ended at')}</TableCell>
            <TableCell>{t_('Status')}</TableCell>
            {anyReason && <TableCell>{t_('Reason')}</TableCell>}
            <TableCell>{t_('Opened by')}</TableCell>
            <TableCell>{t_('Closed by')}</TableCell>
            <TableCell>{t_('Device')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((change) => (
            <TableRow key={change.id} data-testid={`status-change-${change.id}`}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{isBlank(change.changedAt) ? notReported : formatDateTime(change.changedAt)}</TableCell>
              {/* ⚠️⚠️ UN TRAMO ABIERTO NO ES UNA FECHA QUE FALTE. Se lee de `open`, que decidió la
                  frontera, y se dice con palabras: «estado actual», nunca una raya —una raya diría «no
                  se sabe» de algo que se sabe: que todavía no ha terminado—. Y NUNCA «en curso»: el
                  abierto es el estado en que se quedó, y puede ser «Finished». */}
              <TableCell data-testid='status-change-ended' data-open={change.open ? 'true' : 'false'} sx={{ whiteSpace: 'nowrap', fontWeight: change.open ? 700 : 400, color: change.open ? 'info.main' : 'text.primary' }}>
                {change.open ? t_('Current status') : formatDateTime(change.endedAt)}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  {systemValueLabel(t_, 'fabrication_order_status', change.status) || change.status.code || notReported}
                  {/* ⚠️ `forced` llega siempre, también en `false`, pero SOLO SE PINTA CUANDO ES
                      CIERTO: un «no forzado» en cada fila es ruido, y lo que informa es la
                      excepción. Servirlo siempre y pintarlo solo cuando es cierto no se contradice:
                      una es regla de contrato y la otra de pintado. */}
                  {change.forced && <Chip data-testid='status-change-forced' size='small' label={t_('Forced')} sx={{ fontWeight: 700 }} />}
                </Box>
              </TableCell>
              {anyReason && <TableCell data-testid='status-change-reason'>{systemValueLabel(t_, 'reason', change.reason) || change.reason.code || notReported}</TableCell>}
              {/* Los DOS operarios, en columnas separadas: pueden ser personas distintas —uno deja la
                  máquina en un estado y otro la saca—, y juntarlos en una celda escondería justo eso. */}
              <TableCell data-testid='status-change-opened-by'>{personOf(change.openedBy)}</TableCell>
              {/* Un tramo abierto todavía no tiene quién lo cierre: no es un dato ausente, es que aún
                  no ha pasado. ⚠️ Pero NO se deja en blanco —una celda vacía no dice nada (RN8)—: el
                  guion apagado, al lado de un «en curso» en la misma fila, se lee «nadie todavía». */}
              <TableCell data-testid='status-change-closed-by' sx={{ color: change.open ? 'text.disabled' : 'text.primary' }}>
                {change.open ? '—' : personOf(change.closedBy)}
              </TableCell>
              <TableCell>{change.deviceName || notReported}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!anyReason && (
        <Typography data-testid='status-changes-no-reason' variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t_('None of these status changes has a reason recorded')}
        </Typography>
      )}
    </TableContainer>
  )
}

export default StatusChangeTable
